/**
 * Lectura de mecánicas y vista previa de un cambio de documento.
 *
 * Va por las rutas DERIVADAS DEL CATÁLOGO (§6), las mismas que usa el agente.
 * Editar aquí NO es un atajo: llama a `preview_mechanic_change` igual que
 * llamaría el agente, así que el diff y la clasificación son los mismos. Si
 * hubiera una vía propia, una de las dos se saltaría la trazabilidad.
 */
import { queryOptions } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { operations } from "../lib/api-types.ts";
import { collectPages } from "../lib/pages.ts";

type ListResponse =
  operations["list_mechanics"]["responses"][200]["content"]["application/json"]["data"];

export type MechanicSummary = ListResponse["mechanics"][number];

type DetailResponse =
  operations["get_mechanic"]["responses"][200]["content"]["application/json"]["data"];

export type MechanicView = DetailResponse;
export type MechanicVersionSummary = DetailResponse["versions"][number];
export type MechanicAttachment = DetailResponse["attachments"][number];

type PreviewResponse =
  operations["preview_mechanic_change"]["responses"][200]["content"]["application/json"]["data"];

export type ChangePreview = PreviewResponse;

/**
 * Las mecánicas, TODAS: la lista de la memoria dice cuántas hay y el
 * Resumen avisa de la del mes sin publicar, y ninguna de las dos cosas se
 * puede decir con la primera página (1.3).
 */
export const listMechanics = (period?: string): Promise<ListResponse> =>
  collectPages(
    (offset) => {
      const params = new URLSearchParams({ offset: String(offset) });
      if (period !== undefined && period !== "") params.set("period", period);
      return request<ListResponse>(
        `${API_BASE}/queries/list_mechanics?${params.toString()}`,
      );
    },
    (pages) => ({
      ...(pages.at(-1) ?? EMPTY_MECHANICS),
      mechanics: pages.flatMap((page) => page.mechanics),
      offset: 0,
    }),
  );

const EMPTY_MECHANICS: ListResponse = {
  mechanics: [],
  total: 0,
  offset: 0,
  nextOffset: null,
};

export const getMechanic = (
  period: string,
  slug: string,
  number?: number,
): Promise<DetailResponse> => {
  const params = new URLSearchParams({ period, slug });
  if (number !== undefined) params.set("number", String(number));

  return request<DetailResponse>(
    `${API_BASE}/queries/get_mechanic?${params.toString()}`,
  );
};

export const previewChange = (input: {
  period: string;
  slug: string;
  spec: string;
  code: string;
}): Promise<PreviewResponse> => {
  const params = new URLSearchParams(input);
  return request<PreviewResponse>(
    `${API_BASE}/queries/preview_mechanic_change?${params.toString()}`,
  );
};

export const mechanicsQuery = (period?: string) =>
  queryOptions({
    queryKey: ["mechanics", period ?? null] as const,
    queryFn: () => listMechanics(period),
  });

export const mechanicQuery = (period: string, slug: string, number?: number) =>
  queryOptions({
    queryKey: ["mechanic", period, slug, number ?? null] as const,
    queryFn: () => getMechanic(period, slug, number),
  });

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Borrador",
  PUBLISHED: "Vigente",
  SUPERSEDED: "Sustituida",
};

export const statusLabel = (status: string): string =>
  STATUS_LABELS[status] ?? status;

const KIND_LABELS: Record<string, string> = {
  new: "Sería la primera versión",
  unchanged: "No cambia nada",
  editorial: "Solo cambia la explicación",
  rule_candidate: "Cambia el cálculo",
};

export const changeLabel = (kind: string): string => KIND_LABELS[kind] ?? kind;

/** Los tamaños en algo que se lee de un vistazo. */
export const fileSize = (bytes: number): string =>
  bytes < 1024
    ? `${String(bytes)} B`
    : bytes < 1024 * 1024
      ? `${String(Math.round(bytes / 1024))} KB`
      : `${String(Math.round(bytes / 1024 / 102.4) / 10)} MB`;
